<script setup lang="ts">
import Vue, {onMounted, ref} from "vue";
import WorkLocationEnum from "@/enums/User/WorkLocation/WorkLocationEnum.ts";
import LocalStorageService from "@/utils/Services/LocalStorageService.js";
import moment from "moment/moment";
import useApi from "@/modules/api/composables/useApi.ts";

const saveMomentFormat = 'YYYY-MM-DD HH:mm:ss';
const open = ref(false);
const loading = ref(false);

onMounted(() => {
    validateAndShowDialog();
})

const save = async function (workLocation: WorkLocationEnum | null) {
    loading.value = true;
    const now = moment();

    if (workLocation === null) {
        LocalStorageService.set('last_set_work_location', moment().format(saveMomentFormat));
        loading.value = false;
        open.value = false;
        return;
    }

    try {
        await useApi().url('user/work-location').data({
            location: workLocation,
            date: now.format('YYYY-MM-DD'),
            user_id: Vue.prototype.$loggedInUserId,
        }).post().single();

        LocalStorageService.set('last_set_work_location', now.format(saveMomentFormat));
        open.value = false;
        loading.value = false;
    } catch {
        loading.value = false;
    }
}

const validateAndShowDialog = async function (): Promise<WorkLocationEnum | undefined> {
    const lastAutomatedWorkLocation: string | null = LocalStorageService.get('last_set_work_location');
    const now = moment();

    if (
        lastAutomatedWorkLocation !== null &&
        moment(lastAutomatedWorkLocation, saveMomentFormat).isSame(now, 'day')
    ) {
        return;
    }

    const isInWorkingHours = now.isBetween(
        moment('09:00', 'HH:mm'),
        moment('17:30', 'HH:mm')
    );

    if (!isInWorkingHours) {
        return;
    }

    const existingLocation = await useApi().url('user/work-location', {
        user_id: Vue.prototype.$loggedInUserId,
        date: now.format('YYYY-MM-DD'),
    }).first();

    if (existingLocation) {
        LocalStorageService.set('last_set_work_location', now.format(saveMomentFormat));
        return;
    }

    open.value = true;
}
</script>

<template>
    <v-dialog v-model="open" :width="500" persistent>
        <v-card>
            <v-toolbar
                color="primary"
                dark
                flat
            >
                <v-toolbar-title>
                    Werkplekregistratie
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <h3 class="my-4">Welke vergoeding heb jij vandaag recht op?</h3>

                <div style="min-height: 210px">
                    <div v-if="loading" class="text-center">
                        <v-icon color="primary" x-large>mdi-loading mdi-spin</v-icon>
                    </div>
                    <div v-else>
                        <v-btn color="primary" x-large dark block class="mb-4"  @click="save(WorkLocationEnum.OFFICE)">
                            <v-icon left>mdi-office-building</v-icon>
                            Reiskosten
                        </v-btn>
                        <v-btn color="brown" x-large dark block class="mb-4" @click="save(WorkLocationEnum.HOME)">
                            <v-icon left>mdi-home</v-icon>
                            Thuiswerk
                        </v-btn>
                        <v-btn color="grey" x-large dark block @click="save(null)">
                            <v-icon left>mdi-cancel</v-icon>
                            Geen
                        </v-btn>
                    </div>
                </div>

                <p class="mt-4">
                    Voorkom deze melding door de hele week alvast in te vullen via jouw dashboard.
                </p>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>
